// components/EscalationsClaimsPage.js
// Escalations & Claims — operational dashboard + case register.
// Routes: /escalations-claims and /escalations-claims/new (auto-opens creation drawer).
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert, Button, Checkbox, DatePicker, Dropdown, Empty, Input, Popover, Select, Space,
  Spin, Table, Tag, Tooltip, Typography, message,
} from "antd";
import {
  AlertOutlined, BarChartOutlined, CheckCircleOutlined, ClockCircleOutlined, ColumnHeightOutlined,
  EditOutlined, EllipsisOutlined, EyeOutlined, FileExcelOutlined, FilterOutlined, InboxOutlined, PlusOutlined,
  ReloadOutlined, SearchOutlined, StopOutlined, SyncOutlined, WarningOutlined,
} from "@ant-design/icons";
import Cookies from "js-cookie";
import {
  ACTION_TYPES, CARRIERS, CHANNELS, QUICK_VIEWS, STATUSES,
  downloadExport, errMsg, fetchCases, fetchKpis, fetchOptions,
} from "../services/escalationService";
import {
  EscalationFormDrawer, FollowUpModal, OrderHistoryModal, RejectModal, ResolveModal,
  fmtDate, fmtDateTime, statusTag,
} from "./EscalationModals";
import "./EscalationsClaims.css";

const { RangePicker } = DatePicker;
const { Text } = Typography;

const EMPTY_FILTERS = {
  search: "", carrier: null, actionType: null, status: null, escalatedVia: null,
  assignedTo: null, issue: "", escalationRange: null, nextRange: null,
};

const KPI_META = [
  { key: "todaysFollowUps", label: "Today's Follow-ups", icon: <ClockCircleOutlined />, tone: "green", view: "today" },
  { key: "overdue", label: "Overdue", icon: <WarningOutlined />, tone: "red", view: "overdue" },
  { key: "unresolvedIssues", label: "Unresolved Issues", icon: <AlertOutlined />, tone: "blue", view: "unresolvedIssues" },
  { key: "unresolvedClaims", label: "Unresolved Claims", icon: <InboxOutlined />, tone: "violet", view: "unresolvedClaims" },
  { key: "waitingForResponse", label: "Waiting for Response", icon: <SyncOutlined />, tone: "amber", view: "waiting" },
  { key: "resolvedThisMonth", label: "Resolved This Month", icon: <CheckCircleOutlined />, tone: "teal", view: "resolved" },
];

const DEFAULT_VISIBLE_COLS = [
  "sno", "escalationId", "salesOrderId", "awbCode", "issueReason", "carrier",
  "actionType", "escalatedVia", "nextFollowUpDate", "status", "assignedTo", "actions",
];

const ALL_COLUMNS = [
  { key: "sno", label: "S.No" },
  { key: "escalationId", label: "Escalation ID" },
  { key: "salesOrderId", label: "Sales Order ID" },
  { key: "awbCode", label: "AWB" },
  { key: "carrier", label: "Carrier" },
  { key: "issueReason", label: "Issue / Reason" },
  { key: "actionType", label: "Action Type" },
  { key: "escalatedVia", label: "Escalated Via" },
  { key: "escalationDate", label: "Escalation Date" },
  { key: "lastFollowUpDate", label: "Last Follow-up" },
  { key: "nextFollowUpDate", label: "Next Follow-up" },
  { key: "status", label: "Status" },
  { key: "assignedTo", label: "Assigned To" },
  { key: "updatedAt", label: "Updated At" },
  { key: "latestRemarks", label: "Remarks" },
  { key: "actions", label: "Actions" },
];

const EscalationsClaimsPage = ({ darkTheme = false, isMobile = false, autoOpenNew = false }) => {
  const navigate = useNavigate();
  const token = Cookies.get("token") || undefined;

  const [kpis, setKpis] = useState({});
  const [tabs, setTabs] = useState({});
  const [options, setOptions] = useState(null);
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [sort, setSort] = useState({ field: null, order: null });
  const [quickView, setQuickView] = useState("all");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [visibleCols, setVisibleCols] = useState(DEFAULT_VISIBLE_COLS);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerRecord, setDrawerRecord] = useState(null);
  const [followUpRecord, setFollowUpRecord] = useState(null);
  const [resolveRecord, setResolveRecord] = useState(null);
  const [rejectRecord, setRejectRecord] = useState(null);
  const [historyOrder, setHistoryOrder] = useState(null);

  const activeFilterCount = useMemo(
    () => Object.entries(applied).filter(([, v]) => Boolean(v)).length,
    [applied]
  );

  const buildParams = useCallback(() => {
    const p = { page, pageSize };
    if (applied.search) p.search = applied.search;
    if (applied.carrier) p.carrier = applied.carrier;
    if (applied.actionType) p.actionType = applied.actionType;
    if (applied.status) p.status = applied.status;
    if (applied.escalatedVia) p.escalatedVia = applied.escalatedVia;
    if (applied.assignedTo) p.assignedTo = applied.assignedTo;
    if (applied.issue) p.issue = applied.issue;
    if (applied.escalationRange && applied.escalationRange[0]) {
      p.escalationFrom = applied.escalationRange[0].format("YYYY-MM-DD");
      p.escalationTo = applied.escalationRange[1] ? applied.escalationRange[1].format("YYYY-MM-DD") : undefined;
    }
    if (applied.nextRange && applied.nextRange[0]) {
      p.nextFrom = applied.nextRange[0].format("YYYY-MM-DD");
      p.nextTo = applied.nextRange[1] ? applied.nextRange[1].format("YYYY-MM-DD") : undefined;
    }
    if (quickView && quickView !== "all") p.quickView = quickView;
    if (sort.field) { p.sortField = sort.field; p.sortOrder = sort.order === "descend" ? "desc" : "asc"; }
    return p;
  }, [applied, page, pageSize, quickView, sort]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = buildParams();
      const [kpiRes, listRes] = await Promise.all([fetchKpis(token), fetchCases(token, params)]);
      setKpis(kpiRes.kpis || {});
      setTabs(kpiRes.tabs || {});
      setRecords(listRes.records || []);
      setTotal(listRes.total || 0);
    } catch (err) {
      setError(errMsg(err, "Failed to load escalations"));
    } finally {
      setLoading(false);
    }
  }, [buildParams, token]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    fetchOptions(token)
      .then(setOptions)
      .catch((err) => console.warn("Escalation options failed:", errMsg(err)));
  }, [token]);

  // /escalations-claims/new — open the creation form automatically
  useEffect(() => {
    if (autoOpenNew) {
      setDrawerRecord(null);
      setDrawerOpen(true);
    }
  }, [autoOpenNew]);

  const closeDrawer = () => {
    setDrawerOpen(false);
    setDrawerRecord(null);
    if (autoOpenNew && window.location.pathname === "/escalations-claims/new") {
      navigate("/escalations-claims", { replace: true });
    }
  };

  const openNew = () => {
    if (autoOpenNew) { setDrawerOpen(true); setDrawerRecord(null); return; }
    navigate("/escalations-claims/new");
  };

  const applyFilters = () => { setPage(1); setApplied({ ...filters }); };
  const clearFilters = () => { setFilters(EMPTY_FILTERS); setApplied(EMPTY_FILTERS); setPage(1); };

  const handleExport = async () => {
    try {
      await downloadExport(token, "/api/escalations/export", buildParams(), `escalations-register-${new Date().toISOString().slice(0, 10)}.xlsx`);
      message.success("Export downloaded");
    } catch (err) {
      message.error(errMsg(err, "Export failed"));
    }
  };

  const onTableChange = (pagination, filtersArg, sorter) => {
    if (sorter && sorter.order) {
      setSort({ field: sorter.field, order: sorter.order });
      setPage(1);
    } else {
      setSort({ field: null, order: null });
    }
  };

  const onSaved = () => { loadData(); fetchOptions(token).then(setOptions).catch(() => {}); };

  const actionMenu = (record) => ({
    items: [
      { key: "view", icon: <EyeOutlined />, label: "View Details" },
      { key: "edit", icon: <EditOutlined />, label: "Edit Case" },
      { key: "followup", icon: <SyncOutlined />, label: "Add Follow-up", disabled: !record.isOpen },
      { key: "history", icon: <SearchOutlined />, label: "Order History" },
      { type: "divider" },
      { key: "resolve", icon: <CheckCircleOutlined />, label: "Mark Resolved", disabled: !record.isOpen },
      { key: "reject", icon: <StopOutlined />, label: "Mark Rejected", disabled: !record.isOpen },
    ],
    onClick: ({ key }) => {
      if (key === "view") navigate(`/escalations-claims/${record._id}`);
      if (key === "edit") { setDrawerRecord(record); setDrawerOpen(true); }
      if (key === "followup") setFollowUpRecord(record);
      if (key === "history") setHistoryOrder(record.salesOrderId);
      if (key === "resolve") setResolveRecord(record);
      if (key === "reject") setRejectRecord(record);
    },
  });

  const colDef = (key, title, extra = {}) => ({ key, title, dataIndex: key, ...extra });
  const show = (key) => visibleCols.includes(key);

  const allColumns = [
    colDef("sno", "S.No", { width: 44, render: (_, __, i) => (page - 1) * pageSize + i + 1 }),
    colDef("escalationId", "Escalation ID", {
      width: 112,
      render: (v, r) => (
        <a className="ec-link" onClick={(e) => { e.stopPropagation(); navigate(`/escalations-claims/${r._id}`); }}>
          {v}
        </a>
      ),
    }),
    colDef("salesOrderId", "Sales Order ID", {
      width: 108, sorter: true, sortOrder: sort.field === "salesOrderId" ? sort.order : null,
      ellipsis: true, render: (v) => v || "—",
    }),
    colDef("awbCode", "AWB", { width: 96, ellipsis: true, render: (v) => v || "—" }),
    colDef("issueReason", "Issue / Reason", {
      width: 148, ellipsis: true,
      render: (v) => <Tooltip title={v}><span className="ec-cell-ellipsis">{v}</span></Tooltip>,
    }),
    colDef("carrier", "Carrier", { width: 92, ellipsis: true, render: (v, r) => r.carrierDisplay || v || "—" }),
    colDef("actionType", "Action Type", {
      width: 88,
      render: (v) => (
        <Tag className={`ec-type-tag ec-type-${String(v || "").toLowerCase()}`}>{v}</Tag>
      ),
    }),
    colDef("escalatedVia", "Escalated Via", { width: 88, ellipsis: true }),
    colDef("escalationDate", "Escalation Date", {
      width: 100, sorter: true, sortOrder: sort.field === "escalationDate" ? sort.order : null,
      render: (v) => fmtDate(v),
    }),
    colDef("lastFollowUpDate", "Last Follow-up", { width: 100, render: (v) => fmtDate(v) }),
    colDef("nextFollowUpDate", "Next Follow-up", {
      width: 110, sorter: true, sortOrder: sort.field === "nextFollowUpDate" ? sort.order : null,
      render: (v, r) => {
        if (!r.isOpen) return <Text type="secondary">—</Text>;
        if (!v) return <Text type="warning">Not set</Text>;
        return (
          <span className={r.overdue ? "ec-overdue" : "ec-next"}>
            {fmtDate(v)}
            {r.overdue && <Tag className="ec-overdue-tag">+{r.daysOverdue}d</Tag>}
          </span>
        );
      },
    }),
    colDef("status", "Status", {
      width: 140, sorter: true, sortOrder: sort.field === "status" ? sort.order : null,
      render: (v) => statusTag(v),
    }),
    colDef("assignedTo", "Assigned To", { width: 105, ellipsis: true, render: (v) => v || "—" }),
    colDef("updatedAt", "Updated At", {
      width: 120, sorter: true, sortOrder: sort.field === "updatedAt" ? sort.order : null,
      render: (v) => (v ? fmtDateTime(v) : "—"),
    }),
    colDef("latestRemarks", "Remarks", { width: 150, ellipsis: true, render: (v) => <Tooltip title={v}><span className="ec-cell-ellipsis">{v || "—"}</span></Tooltip> }),
    {
      key: "actions", title: "Actions", width: 118, fixed: false,
      render: (_, record) => (
        <Space size={2} className="ec-row-actions" onClick={(e) => e.stopPropagation()}>
          <Tooltip title="View Details">
            <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => navigate(`/escalations-claims/${record._id}`)} aria-label="View details" />
          </Tooltip>
          <Tooltip title="Edit">
            <Button type="text" size="small" icon={<EditOutlined />} onClick={() => { setDrawerRecord(record); setDrawerOpen(true); }} aria-label="Edit case" />
          </Tooltip>
          <Tooltip title={record.isOpen ? "Add Follow-up" : "Case closed"}>
            <Button type="text" size="small" icon={<SyncOutlined />} disabled={!record.isOpen} onClick={() => setFollowUpRecord(record)} aria-label="Add follow-up" />
          </Tooltip>
          <Dropdown menu={actionMenu(record)} trigger={["click"]} placement="bottomRight">
            <Button type="text" size="small" icon={<EllipsisOutlined />} aria-label="More actions" onClick={(e) => e.stopPropagation()} />
          </Dropdown>
        </Space>
      ),
    },
  ].filter((c) => show(c.key));

  const assigneeOpts = (() => {
    const base = [...(options?.assignees || [])];
    (options?.users || []).forEach((u) => { if (u.name && !base.includes(u.name)) base.push(u.name); });
    return base.map((n) => ({ label: n, value: n }));
  })();

  return (
    <div className={`ec-container ${darkTheme ? "ec-dark" : ""}`}>
      {/* Compact header */}
      <div className="ec-head">
        <div className="ec-head-left">
          <h1 className="ec-title">Escalations &amp; Claims</h1>
          <span className="ec-sub">Track delivery issues, claims and follow-ups.</span>
        </div>
        <div className="ec-head-actions">
          <Button size="middle" icon={<ReloadOutlined />} onClick={loadData} loading={loading}>Refresh</Button>
          <Button size="middle" icon={<FileExcelOutlined />} onClick={handleExport}>Export</Button>
          <Button size="middle" icon={<BarChartOutlined />} onClick={() => navigate("/escalations-claims/reports")}>Reports</Button>
          <Button size="middle" type="primary" icon={<PlusOutlined />} onClick={openNew}>New Escalation</Button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="ec-kpi-grid">
        {KPI_META.map((m) => (
          <button
            key={m.key}
            type="button"
            className={`ec-kpi ec-kpi-${m.tone} ${quickView === m.view ? "ec-kpi-active" : ""}`}
            onClick={() => { setQuickView(quickView === m.view ? "all" : m.view); setPage(1); }}
          >
            <span className="ec-kpi-ic">{m.icon}</span>
            <span className="ec-kpi-tx">
              <span className="ec-kpi-value">{kpis[m.key] ?? 0}</span>
              <span className="ec-kpi-label">{m.label}</span>
            </span>
          </button>
        ))}
      </div>

      {/* Quick view tabs */}
      <div className="ec-tabs" role="tablist" aria-label="Quick views">
        {QUICK_VIEWS.map((qv) => (
          <button
            key={qv.key}
            type="button"
            role="tab"
            aria-selected={quickView === qv.key}
            className={`ec-tab ${quickView === qv.key ? "ec-tab-active" : ""}`}
            onClick={() => { setQuickView(qv.key); setPage(1); }}
          >
            {qv.label}
            <span className="ec-tab-count">{tabs[qv.key] ?? 0}</span>
          </button>
        ))}
      </div>

      {/* Filter panel */}
      <div className="ec-filters">
        <div className="ec-fgrid">
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search order / AWB / escalation ID"
            allowClear
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            onPressEnter={applyFilters}
            className="ec-f ec-f-search"
          />
          <Input
            placeholder="Issue / reason"
            allowClear
            value={filters.issue}
            onChange={(e) => setFilters({ ...filters, issue: e.target.value })}
            onPressEnter={applyFilters}
            className="ec-f"
          />
          <Select
            allowClear placeholder="Carrier" value={filters.carrier}
            onChange={(v) => setFilters({ ...filters, carrier: v || null })}
            options={CARRIERS.map((c) => ({ label: c, value: c }))}
            className="ec-f"
          />
          <Select
            allowClear placeholder="Action Type" value={filters.actionType}
            onChange={(v) => setFilters({ ...filters, actionType: v || null })}
            options={ACTION_TYPES.map((c) => ({ label: c, value: c }))}
            className="ec-f"
          />
          <Select
            allowClear placeholder="Status" value={filters.status}
            onChange={(v) => setFilters({ ...filters, status: v || null })}
            options={STATUSES.map((c) => ({ label: c, value: c }))}
            className="ec-f"
          />
          <Select
            allowClear placeholder="Escalated Via" value={filters.escalatedVia}
            onChange={(v) => setFilters({ ...filters, escalatedVia: v || null })}
            options={CHANNELS.map((c) => ({ label: c, value: c }))}
            className="ec-f"
          />
          <Select
            allowClear showSearch placeholder="Assigned To" value={filters.assignedTo}
            onChange={(v) => setFilters({ ...filters, assignedTo: v || null })}
            options={assigneeOpts}
            className="ec-f"
          />
          <RangePicker
            value={filters.escalationRange}
            onChange={(v) => setFilters({ ...filters, escalationRange: v })}
            placeholder={["Esc. from", "Esc. to"]}
            allowClear
            className="ec-f ec-f-date"
          />
          <RangePicker
            value={filters.nextRange}
            onChange={(v) => setFilters({ ...filters, nextRange: v })}
            placeholder={["Next from", "Next to"]}
            allowClear
            className="ec-f ec-f-date"
          />
          <div className="ec-f-btns">
            <Button type="primary" icon={<FilterOutlined />} onClick={applyFilters}>
              Apply{activeFilterCount ? ` (${activeFilterCount})` : ""}
            </Button>
            <Button onClick={clearFilters}>Clear</Button>
          </div>
        </div>
      </div>

      {/* Register */}
      <div className="ec-register">
        <div className="ec-register-head">
          <h2>Case Register <span className="ec-total">{total} case{total === 1 ? "" : "s"}</span></h2>
          <div className="ec-register-head-right">
            <Popover
              placement="bottomRight"
              trigger="click"
              content={
                <div className="ec-col-pop">
                  {ALL_COLUMNS.map((c) => (
                    <Checkbox
                      key={c.key}
                      checked={visibleCols.includes(c.key)}
                      disabled={c.key === "actions"}
                      onChange={(e) => {
                        setVisibleCols((prev) => (e.target.checked
                          ? [...prev, c.key]
                          : prev.filter((k) => k !== c.key)));
                      }}
                    >
                      {c.label}
                    </Checkbox>
                  ))}
                </div>
              }
            >
              <Button size="small" icon={<ColumnHeightOutlined />}>Columns</Button>
            </Popover>
          </div>
        </div>

        {error && (
          <Alert type="error" showIcon message={error} action={<Button size="small" onClick={loadData}>Retry</Button>} style={{ marginBottom: 12 }} />
        )}

        {!isMobile ? (
          <Table
            rowKey={(r) => r._id}
            className="ec-table"
            size="small"
            loading={loading}
            columns={allColumns}
            dataSource={records}
            scroll={{ x: "max-content" }}
            tableLayout="fixed"
            onChange={onTableChange}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
              pageSizeOptions: [15, 25, 50, 100],
              showTotal: (t) => `${t} cases`,
              onChange: (p, ps) => { setPage(p); setPageSize(ps); },
            }}
            locale={{
              emptyText: <Empty description="No escalations match the current filters" image={Empty.PRESENTED_IMAGE_SIMPLE} />,
            }}
            onRow={(record) => ({
              onDoubleClick: () => navigate(`/escalations-claims/${record._id}`),
            })}
          />
        ) : (
          <div className="ec-cards">
            {loading && <div className="ec-cards-loading"><Spin /></div>}
            {!loading && records.length === 0 && <Empty description="No escalations match the current filters" />}
            {records.map((r) => (
              <div key={r._id} className={`ec-card ${r.overdue ? "ec-card-overdue" : ""}`}>
                <div className="ec-card-top">
                  <b onClick={() => navigate(`/escalations-claims/${r._id}`)}>{r.escalationId}</b>
                  {statusTag(r.status)}
                </div>
                <div className="ec-card-line"><span>Order</span><b>{r.salesOrderId}</b></div>
                <div className="ec-card-line"><span>AWB</span><b>{r.awbCode || "—"}</b></div>
                <div className="ec-card-line"><span>Carrier</span><b>{r.carrierDisplay}</b></div>
                <div className="ec-card-line"><span>Issue</span><b>{r.issueReason}</b></div>
                <div className="ec-card-line"><span>Type</span><b>{r.actionType} · via {r.escalatedVia}</b></div>
                <div className="ec-card-line"><span>Assigned</span><b>{r.assignedTo || "—"}</b></div>
                <div className="ec-card-line">
                  <span>Next follow-up</span>
                  <b style={{ color: r.overdue ? "#dc2626" : undefined }}>
                    {r.isOpen ? (r.nextFollowUpDate ? fmtDate(r.nextFollowUpDate) : "Not set") : "—"}
                    {r.overdue ? ` (+${r.daysOverdue}d overdue)` : ""}
                  </b>
                </div>
                <div className="ec-card-actions">
                  <Button size="small" onClick={() => navigate(`/escalations-claims/${r._id}`)}>View</Button>
                  <Button size="small" onClick={() => { setDrawerRecord(r); setDrawerOpen(true); }}>Edit</Button>
                  <Button size="small" disabled={!r.isOpen} onClick={() => setFollowUpRecord(r)}>Follow-up</Button>
                  <Button size="small" onClick={() => setHistoryOrder(r.salesOrderId)}>History</Button>
                </div>
              </div>
            ))}
          </div>
        )}
        {!isMobile && (
          <div className="ec-hint">Tip: double-click a row to open the case detail panel.</div>
        )}
      </div>

      {/* Dialogs */}
      <EscalationFormDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        record={drawerRecord}
        options={options}
        onSaved={onSaved}
        isMobile={isMobile}
      />
      <FollowUpModal open={Boolean(followUpRecord)} record={followUpRecord} onClose={() => setFollowUpRecord(null)} onSaved={onSaved} />
      <ResolveModal open={Boolean(resolveRecord)} record={resolveRecord} onClose={() => setResolveRecord(null)} onSaved={onSaved} />
      <RejectModal open={Boolean(rejectRecord)} record={rejectRecord} onClose={() => setRejectRecord(null)} onSaved={onSaved} />
      <OrderHistoryModal open={Boolean(historyOrder)} salesOrderId={historyOrder} onClose={() => setHistoryOrder(null)} darkTheme={darkTheme} />
    </div>
  );
};

export default EscalationsClaimsPage;




